// Analitico.js - VERSÃO VENDEDOR (SaaS) + ADMIN (Empresa)
// 🔥 Superadmin: vê métricas de negócio (empresas, receita, licenças, planos)
// 🔥 Admin: vê métricas da sua empresa (utilizadores, vagas, ocupação)
// 🔥 TODOS OS ÍCONES SÃO SVG DO SharedComponents (sem emojis)
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import './Analitico.css';
import { Avatar, ProgressCircle, Icon } from './SharedComponents';

export default function Analitico({ getRelativeTime }) {
    // ==============================================
    // ESTADOS PARA UTILIZADORES (ADMIN)
    // ==============================================
    const [usersList, setUsersList] = useState([]);
    const [empresasList, setEmpresasList] = useState([]);
    const [showAllUsersLocal, setShowAllUsersLocal] = useState(false);
    const [showUserModalLocal, setShowUserModalLocal] = useState(false);
    const [showUserDetailsModalLocal, setShowUserDetailsModalLocal] = useState(false);
    const [selectedUserDetailsLocal, setSelectedUserDetailsLocal] = useState(null);
    const [isEditingUserLocal, setIsEditingUserLocal] = useState(false);
    const [editUserDataLocal, setEditUserDataLocal] = useState({
        nome: '', email: '', perfil: '', empresaId: '', empresaNome: '', senha: ''
    });
    const [isLoadingUserDetailsLocal, setIsLoadingUserDetailsLocal] = useState(false);
    const [showPasswordFieldLocal, setShowPasswordFieldLocal] = useState(false);
    const [selectedImageLocal, setSelectedImageLocal] = useState(null);
    const [imagePreviewLocal, setImagePreviewLocal] = useState(null);
    const [editSelectedImageLocal, setEditSelectedImageLocal] = useState(null);
    const [editImagePreviewLocal, setEditImagePreviewLocal] = useState(null);
    const [isUploadingImageLocal, setIsUploadingImageLocal] = useState(false);
    const [userFormErrorLocal, setUserFormErrorLocal] = useState('');
    const [isAddingUserLocal, setIsAddingUserLocal] = useState(false);
    const [newUserLocal, setNewUserLocal] = useState({
        nome: '', email: '', perfil: 'Cliente', empresaId: '', empresaNome: '', senha: ''
    });
    const [tempToastLocal, setTempToastLocal] = useState(null);
    const [isSuperadmin, setIsSuperadmin] = useState(false);
    const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);

    // ==============================================
    // 🔥 NOVOS ESTADOS — MÉTRICAS SAAS (SUPERADMIN)
    // ==============================================
    const [licencasList, setLicencasList] = useState([]);
    const [loadingMetricas, setLoadingMetricas] = useState(false);
    const [periodoFiltro, setPeriodoFiltro] = useState('30d');

    // ==============================================
    // ESTADO PARA O UTILIZADOR LOGADO
    // ==============================================
    const [loggedUser, setLoggedUser] = useState(null);

    // ==============================================
    // ESTADO PARA LIMITE DE UTILIZADORES
    // ==============================================
    const [limiteUtilizadores, setLimiteUtilizadores] = useState({ limite: 0, utilizadoresAtuais: 0, disponivel: 0 });
    const [loadingLimite, setLoadingLimite] = useState(false);

    // ==============================================
    // 🔥 FUNÇÃO PARA NOTIFICAR ATUALIZAÇÃO
    // ==============================================
    const notificarAtualizacaoUtilizadores = () => {
        window.dispatchEvent(new CustomEvent('utilizadoresAtualizados', { detail: { timestamp: Date.now() } }));
    };

    // ==============================================
    // 🔥 FUNÇÃO PARA CARREGAR LIMITE
    // ==============================================
    const carregarLimiteUtilizadores = async (empresaId) => {
        if (!empresaId) return;
        setLoadingLimite(true);
        try {
            const token = localStorage.getItem('token');
            if (!token) return;
            const response = await axios.get(`/api/licencas/empresa/${empresaId}/limite-utilizadores`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setLimiteUtilizadores(response.data);
        } catch (err) {
            console.error('❌ Erro ao carregar limite:', err);
            setLimiteUtilizadores({ limite: 0, utilizadoresAtuais: 0, disponivel: 0 });
        } finally {
            setLoadingLimite(false);
        }
    };

    // ==============================================
    // 🔥 CARREGAR LICENÇAS (do routes/licencas.js)
    // ==============================================
    const carregarLicencas = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) return [];
            const response = await axios.get('/api/licencas', {
                headers: { Authorization: `Bearer ${token}` }
            });
            const data = response.data?.licencas || response.data?.data || response.data || [];
            const lista = Array.isArray(data) ? data : [];
            setLicencasList(lista);
            console.log('🎫 Licenças carregadas:', lista.length);
            return lista;
        } catch (err) {
            console.error('❌ Erro ao carregar licenças:', err);
            setLicencasList([]);
            return [];
        }
    };

    // ==============================================
    // 🔥 CARREGAR EMPRESAS (do routes/empresas.js)
    // ==============================================
    const carregarEmpresas = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) return [];
            const response = await axios.get('/api/empresas/options/dropdown?all=true', {
                headers: { Authorization: `Bearer ${token}` }
            });
            const data = response.data?.data || [];
            setEmpresasList(data);
            console.log('🏢 Empresas carregadas:', data.length);
            return data;
        } catch (err) {
            console.error('❌ Erro ao carregar empresas:', err);
            try {
                const token = localStorage.getItem('token');
                const fallback = await axios.get('/api/empresas/options/dropdown', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const data = fallback.data?.data || [];
                setEmpresasList(data);
                console.log('🏢 Empresas carregadas (fallback):', data.length);
                return data;
            } catch (err2) {
                console.error('❌ Fallback empresas também falhou:', err2);
                setEmpresasList([]);
                return [];
            }
        }
    };

    // ==============================================
    // 🔥 CARREGAR UTILIZADORES (do routes/auth.js)
    // ==============================================
    const carregarUtilizadores = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) return [];

            const response = await axios.get('/api/auth/users', {
                headers: { Authorization: `Bearer ${token}` }
            });

            const allUsers = response.data?.data || response.data || [];
            const lista = Array.isArray(allUsers) ? allUsers : [];

            const user = loggedUser || JSON.parse(localStorage.getItem('usuario') || '{}');
            let filteredUsers = lista;

            if (user.perfil !== 'Superadmin') {
                const empresaId = extrairEmpresaId(user.empresaId) || extrairEmpresaId(user.empresa);
                if (empresaId) {
                    filteredUsers = lista.filter(u => {
                        const userEmpresaId = extrairEmpresaId(u.empresaId) || extrairEmpresaId(u.empresa);
                        return userEmpresaId && userEmpresaId.toString() === empresaId.toString();
                    });
                } else {
                    filteredUsers = lista.filter(u => u._id === user._id);
                }
            }

            const sortedUsers = [...filteredUsers].sort((a, b) => {
                const dateA = new Date(a.dataCriacao || a.createdAt || 0);
                const dateB = new Date(b.dataCriacao || b.createdAt || 0);
                if (dateA.getTime() && dateB.getTime() && dateA.getTime() !== dateB.getTime()) return dateB - dateA;
                if (a._id && b._id) return b._id.localeCompare(a._id);
                return 0;
            });

            setUsersList(sortedUsers);
            console.log('👥 Utilizadores carregados:', sortedUsers.length);
            return sortedUsers;
        } catch (err) {
            console.error('❌ Erro ao carregar utilizadores:', err);
            setUsersList([]);
            return [];
        }
    };

    // ==============================================
    // 🔥 CARREGAR TODOS OS DADOS EM PARALELO
    // ==============================================
    const carregarDadosAnalitico = async () => {
        setLoadingMetricas(true);
        try {
            const token = localStorage.getItem('token');
            if (!token) return;

            const headers = { Authorization: `Bearer ${token}` };

            console.log('🚀 A carregar dados do Analítico em paralelo...');

            const [empresasRes, usersRes, licencasRes] = await Promise.all([
                axios.get('/api/empresas/options/dropdown?all=true', { headers })
                    .catch(() => {
                        console.warn('⚠️ Fallback empresas sem ?all=true');
                        return axios.get('/api/empresas/options/dropdown', { headers })
                            .catch(() => ({ data: { data: [] } }));
                    }),
                axios.get('/api/auth/users', { headers })
                    .catch(() => ({ data: { data: [] } })),
                axios.get('/api/licencas', { headers })
                    .catch(() => ({ data: { licencas: [] } }))
            ]);

            const empresas = empresasRes.data?.data || [];
            const usersAll = usersRes.data?.data || usersRes.data || [];
            const licencas = licencasRes.data?.licencas || licencasRes.data?.data || [];

            const user = loggedUser || JSON.parse(localStorage.getItem('usuario') || '{}');
            let usersFiltrados = Array.isArray(usersAll) ? usersAll : [];

            if (user.perfil !== 'Superadmin') {
                const empresaId = extrairEmpresaId(user.empresaId) || extrairEmpresaId(user.empresa);
                if (empresaId) {
                    usersFiltrados = usersFiltrados.filter(u => {
                        const userEmpresaId = extrairEmpresaId(u.empresaId) || extrairEmpresaId(u.empresa);
                        return userEmpresaId && userEmpresaId.toString() === empresaId.toString();
                    });
                } else {
                    usersFiltrados = usersFiltrados.filter(u => u._id === user._id);
                }
            }

            usersFiltrados.sort((a, b) => {
                const dateA = new Date(a.dataCriacao || a.createdAt || 0);
                const dateB = new Date(b.dataCriacao || b.createdAt || 0);
                if (dateA.getTime() && dateB.getTime() && dateA.getTime() !== dateB.getTime()) return dateB - dateA;
                if (a._id && b._id) return b._id.localeCompare(a._id);
                return 0;
            });

            setEmpresasList(empresas);
            setUsersList(usersFiltrados);
            setLicencasList(Array.isArray(licencas) ? licencas : []);

            console.log('✅ Dados carregados:');
            console.log('  - Empresas:', empresas.length);
            console.log('  - Utilizadores:', usersFiltrados.length);
            console.log('  - Licenças:', licencas.length);

            if (user.perfil !== 'Superadmin' && user.empresaId) {
                const empId = extrairEmpresaId(user.empresaId) || extrairEmpresaId(user.empresa);
                if (empId) await carregarLimiteUtilizadores(empId);
            }
        } catch (err) {
            console.error('❌ Erro ao carregar dados do analítico:', err);
            showTempToast('Erro', 'Não foi possível carregar os dados', 'danger');
        } finally {
            setLoadingMetricas(false);
        }
    };

    // ==============================================
    // 🔥 MÉTRICAS SAAS CALCULADAS (useMemo)
    // ==============================================
    const metricasSaas = useMemo(() => {
        const hoje = new Date();
        const agora = new Date();

        const totalEmpresas = empresasList.length;
        const empresasAtivas = empresasList.filter(e => !e.arquivado && e.status !== 'Suspensa').length;
        const empresasArquivadas = empresasList.filter(e => e.arquivado === true).length;

        const totalLicencas = licencasList.length;
        const licencasAtivas = licencasList.filter(l => l.ativa === true).length;

        const licencasAExpirar = licencasList.filter(l => {
            if (!l.dataFim || !l.ativa) return false;
            const dias = Math.ceil((new Date(l.dataFim) - agora) / (1000 * 60 * 60 * 24));
            return dias > 0 && dias <= 30;
        }).length;

        const licencasExpiradas = licencasList.filter(l => {
            if (!l.dataFim) return false;
            return new Date(l.dataFim) < agora;
        }).length;

        const planos = { Trial: 0, Basico: 0, Profissional: 0, Enterprise: 0, Custom: 0 };
        licencasList.forEach(l => {
            const p = l.plano || 'Basico';
            if (planos[p] !== undefined) planos[p]++;
        });

        const precos = { Trial: 0, Basico: 1000, Profissional: 1500, Enterprise: 2500, Custom: 0 };
        const receitaMensal = Object.entries(planos).reduce((acc, [plano, qtd]) => {
            return acc + (precos[plano] || 0) * qtd;
        }, 0);

        const totalUtilizadores = usersList.length;
        const utilizadoresAtivos = usersList.filter(u => u.ativo !== false).length;

        const novos30d = usersList.filter(u => {
            const dataCriacao = u.dataCriacao || u.createdAt;
            if (!dataCriacao) return false;
            const diff = (hoje - new Date(dataCriacao)) / (1000 * 60 * 60 * 24);
            return diff <= 30;
        }).length;

        return {
            totalEmpresas,
            empresasAtivas,
            empresasArquivadas,
            totalLicencas,
            licencasAtivas,
            licencasAExpirar,
            licencasExpiradas,
            planos,
            receitaMensal,
            totalUtilizadores,
            utilizadoresAtivos,
            novos30d
        };
    }, [empresasList, licencasList, usersList]);

    // ==============================================
    // 🔥 FUNÇÃO PARA GERIR ASSOCIAÇÃO
    // ==============================================
    const gerenciarAssociacaoEmpresa = async (userId, novaEmpresaId, novaEmpresaNome) => {
        try {
            const token = localStorage.getItem('token');
            if (!token) return false;
            const userResponse = await axios.get(`/api/auth/users`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const users = userResponse.data?.data || userResponse.data || [];
            const user = users.find(u => u._id === userId);
            if (!user) return false;

            let empresaAtualId = null;
            if (user.empresaId) empresaAtualId = user.empresaId;
            else if (user.empresa && typeof user.empresa === 'object' && user.empresa._id) empresaAtualId = user.empresa._id;

            if (!novaEmpresaId) {
                if (empresaAtualId) {
                    try {
                        await axios.delete(`/api/empresas/${empresaAtualId}/utilizadores/${userId}`, {
                            headers: { Authorization: `Bearer ${token}` }
                        });
                        return true;
                    } catch (err) { return false; }
                }
                return true;
            }

            if (empresaAtualId === novaEmpresaId) return true;

            if (empresaAtualId) {
                try {
                    await axios.delete(`/api/empresas/${empresaAtualId}/utilizadores/${userId}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                } catch (err) { console.error(err); }
            }

            try {
                await axios.post(`/api/empresas/${novaEmpresaId}/utilizadores`, { userId }, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                return true;
            } catch (err) { return false; }
        } catch (err) {
            console.error('Erro ao gerenciar associação:', err);
            return false;
        }
    };

    // ==============================================
    // HELPERS
    // ==============================================
    const extrairEmpresaId = (empresa) => {
        if (!empresa) return null;
        if (typeof empresa === 'string') return empresa;
        if (typeof empresa === 'object' && empresa._id) return empresa._id;
        if (typeof empresa === 'object' && empresa.id) return empresa.id;
        return null;
    };

    const atualizarLoggedUser = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) return;
            const response = await axios.get('/api/auth/users/me', {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data && response.data.success) {
                setLoggedUser(response.data.data);
                return response.data.data;
            }
        } catch (err) {
            console.error('❌ Erro ao atualizar logged user:', err);
        }
        return null;
    };

    const fetchEmpresas = async () => {
        return await carregarEmpresas();
    };

    const fetchUsers = async () => {
        return await carregarUtilizadores();
    };

    const podeAdicionarUtilizador = () => {
        if (isSuperadmin) return true;
        if (limiteUtilizadores.limite === 0) return false;
        return limiteUtilizadores.disponivel > 0;
    };

    const getLimiteMessage = () => {
        if (isSuperadmin) return null;
        if (limiteUtilizadores.limite === 0) {
            return 'Esta empresa não tem uma licença ativa. Contacte o administrador.';
        }
        if (limiteUtilizadores.disponivel <= 0) {
            return `Limite de utilizadores atingido (${limiteUtilizadores.utilizadoresAtuais}/${limiteUtilizadores.limite}). Contacte o administrador para aumentar o limite.`;
        }
        return `${limiteUtilizadores.disponivel} vaga(s) disponível(eis) (${limiteUtilizadores.utilizadoresAtuais}/${limiteUtilizadores.limite})`;
    };

    // ==============================================
    // EVENTOS
    // ==============================================
    useEffect(() => {
        const handleAssociacaoAlterada = () => {
            fetchUsers();
            fetchEmpresas();
            carregarLicencas();
        };
        const handleUtilizadoresAtualizados = () => {
            fetchUsers();
            fetchEmpresas();
        };
        window.addEventListener('associacaoEmpresaUtilizadorAlterada', handleAssociacaoAlterada);
        window.addEventListener('utilizadoresAtualizados', handleUtilizadoresAtualizados);
        return () => {
            window.removeEventListener('associacaoEmpresaUtilizadorAlterada', handleAssociacaoAlterada);
            window.removeEventListener('utilizadoresAtualizados', handleUtilizadoresAtualizados);
        };
    }, []);

    // ==============================================
    // CARREGAR DADOS INICIAIS
    // ==============================================
    useEffect(() => {
        const init = async () => {
            const user = JSON.parse(localStorage.getItem('usuario') || '{}');
            const isSuper = user.perfil === 'Superadmin';
            setIsSuperadmin(isSuper);

            await atualizarLoggedUser();
            await carregarDadosAnalitico();
        };
        init();
    }, []);

    // ==============================================
    // PRÉ-SELECIONAR EMPRESA NO MODAL
    // ==============================================
    useEffect(() => {
        if (showUserModalLocal && !isSuperadmin) {
            const user = loggedUser || JSON.parse(localStorage.getItem('usuario') || '{}');
            const empresaId = extrairEmpresaId(user.empresaId) || extrairEmpresaId(user.empresa);
            if (empresaId && !newUserLocal.empresaId) {
                const empresa = empresasList.find(e => e._id === empresaId);
                if (empresa) {
                    setNewUserLocal(prev => ({ ...prev, empresaId, empresaNome: empresa.nome || '' }));
                }
            }
        }
    }, [showUserModalLocal, empresasList, isSuperadmin, loggedUser]);

    // ==============================================
    // HELPERS DE FORMATAÇÃO
    // ==============================================
    const getDaysSinceCreation = (dataCriacao) => {
        if (!dataCriacao) return 'Data desconhecida';
        const createdDate = new Date(dataCriacao);
        const today = new Date();
        createdDate.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);
        const diffTime = today - createdDate;
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        if (diffDays === 0) return 'Hoje';
        if (diffDays === 1) return 'Há 1 dia';
        return `Há ${diffDays} dias`;
    };

    const showTempToast = (title, message, type = 'success') => {
        setTempToastLocal({ title, message, type });
        setTimeout(() => setTempToastLocal(null), 3000);
    };

    const getImagemUrl = (imagemPerfil) => {
        if (!imagemPerfil) return null;
        if (imagemPerfil.startsWith('http://') || imagemPerfil.startsWith('https://')) return imagemPerfil;
        let cleanPath = imagemPerfil.replace(/^\/+/, '');
        if (cleanPath.startsWith('uploads/')) return `/${cleanPath}`;
        if (!cleanPath.startsWith('uploads/')) cleanPath = `uploads/${cleanPath}`;
        return `/${cleanPath}`;
    };

    const uploadImage = async (userId, file) => {
        const formData = new FormData();
        formData.append('imagemPerfil', file);
        try {
            const token = localStorage.getItem('token');
            if (!token) throw new Error('Token não encontrado');
            const response = await axios.put(`/api/auth/users/${userId}/imagem`, formData, {
                headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` }
            });
            await fetchUsers();
            return response.data;
        } catch (err) {
            console.error('❌ Erro ao fazer upload da imagem:', err);
            throw err;
        }
    };

    const handleImageChange = (e, isEdit = false) => {
        const file = e.target.files[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            showTempToast('Erro', 'Por favor, selecione uma imagem válida', 'danger');
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            showTempToast('Erro', 'A imagem não pode exceder 5MB', 'danger');
            return;
        }
        const reader = new FileReader();
        if (isEdit) {
            setEditSelectedImageLocal(file);
            reader.onloadend = () => setEditImagePreviewLocal(reader.result);
        } else {
            setSelectedImageLocal(file);
            reader.onloadend = () => setImagePreviewLocal(reader.result);
        }
        reader.readAsDataURL(file);
    };

    const getEmpresaInfoFromUser = (user) => {
        let empresaId = '', empresaNome = '';
        const id = extrairEmpresaId(user.empresaId) || extrairEmpresaId(user.empresa);
        if (id) empresaId = id;
        if (user.empresaNome) empresaNome = user.empresaNome;
        else if (user.empresa && typeof user.empresa === 'string') empresaNome = user.empresa;
        else if (user.empresa && typeof user.empresa === 'object' && user.empresa.nome) empresaNome = user.empresa.nome;
        if (empresaId && !empresaNome && empresasList.length > 0) {
            const foundEmpresa = empresasList.find(e => e._id === empresaId);
            if (foundEmpresa) empresaNome = foundEmpresa.nome;
        }
        return { empresaId, empresaNome };
    };

    // ==============================================
    // FETCH USER DETAILS
    // ==============================================
    const fetchUserDetailsLocal = async (userId, userName) => {
        setIsLoadingUserDetailsLocal(true);
        setSelectedUserDetailsLocal(null);
        setIsEditingUserLocal(false);
        setEditSelectedImageLocal(null);
        setEditImagePreviewLocal(null);
        setShowPasswordFieldLocal(false);
        try {
            const token = localStorage.getItem('token');
            if (!token) return;
            const userResponse = await axios.get(`/api/auth/users/${userId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (userResponse.data && userResponse.data.success) {
                const user = userResponse.data.data;
                setSelectedUserDetailsLocal(user);
                const { empresaId, empresaNome } = getEmpresaInfoFromUser(user);
                let empresaIdFinal = empresaId;
                if (!empresaIdFinal && empresaNome) {
                    const foundEmpresa = empresasList.find(e => e.nome === empresaNome);
                    if (foundEmpresa) empresaIdFinal = foundEmpresa._id;
                }
                if (!empresaIdFinal) {
                    const id = extrairEmpresaId(user.empresaId) || extrairEmpresaId(user.empresa);
                    if (id) empresaIdFinal = id;
                }
                setEditUserDataLocal({
                    nome: user.nome || '',
                    email: user.email || '',
                    perfil: user.perfil || 'Cliente',
                    empresaId: empresaIdFinal || '',
                    empresaNome: empresaNome || '',
                    senha: ''
                });
            } else {
                showTempToast('Erro', 'Não foi possível carregar os detalhes do utilizador', 'danger');
            }
            setShowUserDetailsModalLocal(true);
        } catch (err) {
            console.error('Erro ao buscar detalhes do utilizador:', err);
            showTempToast('Erro', 'Não foi possível carregar os detalhes do utilizador', 'danger');
        } finally {
            setIsLoadingUserDetailsLocal(false);
        }
    };

    // ==============================================
    // PERMISSÕES DE EDIÇÃO/APAGAR
    // ==============================================
    const podeEditarUtilizador = () => {
        if (isSuperadmin) return true;
        if (!selectedUserDetailsLocal) return false;
        const loggedUserId = loggedUser?._id || loggedUser?.id;
        const selectedUserId = selectedUserDetailsLocal._id || selectedUserDetailsLocal.id;
        if (loggedUserId && selectedUserId && loggedUserId.toString() === selectedUserId.toString()) return false;
        if (loggedUser?.perfil === 'Admin' && selectedUserDetailsLocal.perfil === 'Admin') return false;
        if (loggedUser?.perfil === 'Admin' && selectedUserDetailsLocal.perfil === 'Superadmin') return false;
        if (loggedUser?.perfil !== 'Admin' && !isSuperadmin) return false;
        const loggedUserEmpresaId = extrairEmpresaId(loggedUser?.empresaId) || extrairEmpresaId(loggedUser?.empresa);
        const userEmpresaId = extrairEmpresaId(selectedUserDetailsLocal.empresaId) || extrairEmpresaId(selectedUserDetailsLocal.empresa);
        if (loggedUserEmpresaId && userEmpresaId) return loggedUserEmpresaId.toString() === userEmpresaId.toString();
        if (loggedUserEmpresaId && !userEmpresaId) return true;
        if (!loggedUserEmpresaId) return true;
        return false;
    };

    const podeApagarUtilizador = () => {
        if (isSuperadmin) return true;
        if (!selectedUserDetailsLocal) return false;
        const loggedUserId = loggedUser?._id || loggedUser?.id;
        const selectedUserId = selectedUserDetailsLocal._id || selectedUserDetailsLocal.id;
        if (loggedUserId && selectedUserId && loggedUserId.toString() === selectedUserId.toString()) return false;
        if (loggedUser?.perfil === 'Admin' && selectedUserDetailsLocal.perfil === 'Admin') return false;
        if (loggedUser?.perfil === 'Admin' && selectedUserDetailsLocal.perfil === 'Superadmin') return false;
        if (loggedUser?.perfil !== 'Admin' && !isSuperadmin) return false;
        const loggedUserEmpresaId = extrairEmpresaId(loggedUser?.empresaId) || extrairEmpresaId(loggedUser?.empresa);
        const userEmpresaId = extrairEmpresaId(selectedUserDetailsLocal.empresaId) || extrairEmpresaId(selectedUserDetailsLocal.empresa);
        if (loggedUserEmpresaId && userEmpresaId) return loggedUserEmpresaId.toString() === userEmpresaId.toString();
        if (loggedUserEmpresaId && !userEmpresaId) return true;
        return false;
    };

    // ==============================================
    // UPDATE USER
    // ==============================================
    const updateUser = async () => {
        if (!editUserDataLocal.nome.trim()) { showTempToast('Erro', 'O nome não pode estar vazio', 'danger'); return; }
        if (!editUserDataLocal.email.trim()) { showTempToast('Erro', 'O email não pode estar vazio', 'danger'); return; }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editUserDataLocal.email)) { showTempToast('Erro', 'Por favor, insira um email válido', 'danger'); return; }
        if (editUserDataLocal.senha && editUserDataLocal.senha.length < 6) { showTempToast('Erro', 'A senha deve ter no mínimo 6 caracteres', 'danger'); return; }
        if (!podeEditarUtilizador()) { showTempToast('Erro', 'Não tem permissão para editar este utilizador', 'danger'); return; }

        setIsLoadingUserDetailsLocal(true);
        try {
            const token = localStorage.getItem('token');
            if (!token) { showTempToast('Erro', 'Sessão expirada.', 'danger'); setIsLoadingUserDetailsLocal(false); return; }

            const updateData = { nome: editUserDataLocal.nome, email: editUserDataLocal.email.toLowerCase() };

            if (editUserDataLocal.perfil) {
                const perfilSelecionado = editUserDataLocal.perfil;
                if (loggedUser?.perfil === 'Admin') {
                    const perfisPermitidos = ['Cliente', 'Contabilista', 'Comercial'];
                    if (perfisPermitidos.includes(perfilSelecionado)) updateData.perfil = perfilSelecionado;
                    else { showTempToast('Erro', 'Admin só pode atribuir perfis: Cliente, Contabilista ou Comercial', 'danger'); setIsLoadingUserDetailsLocal(false); return; }
                } else if (isSuperadmin) updateData.perfil = perfilSelecionado;
                else if (selectedUserDetailsLocal?.perfil) updateData.perfil = selectedUserDetailsLocal.perfil;
            }

            const empresaAntigaId = extrairEmpresaId(selectedUserDetailsLocal?.empresaId) || extrairEmpresaId(selectedUserDetailsLocal?.empresa);
            if (isSuperadmin) {
                if (editUserDataLocal.empresaId) {
                    updateData.empresaId = editUserDataLocal.empresaId;
                    const selectedEmpresa = empresasList.find(e => e._id === editUserDataLocal.empresaId);
                    if (selectedEmpresa) updateData.empresa = selectedEmpresa.nome;
                } else { updateData.empresaId = null; updateData.empresa = null; }
            } else {
                if (editUserDataLocal.empresaId) {
                    updateData.empresaId = editUserDataLocal.empresaId;
                    const empresaAtual = empresasList.find(e => e._id === editUserDataLocal.empresaId);
                    if (empresaAtual) updateData.empresa = empresaAtual.nome;
                } else { updateData.empresaId = null; updateData.empresa = null; }
            }

            if (editUserDataLocal.senha) updateData.senha = editUserDataLocal.senha;

            const response = await axios.put(`/api/auth/users/${selectedUserDetailsLocal._id}`, updateData, {
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
            });

            if (response.data && response.data.success) {
                const empresaNovaId = updateData.empresaId || null;
                if (empresaAntigaId !== empresaNovaId) {
                    if (empresaAntigaId) {
                        try { await axios.delete(`/api/empresas/${empresaAntigaId}/utilizadores/${selectedUserDetailsLocal._id}`, { headers: { Authorization: `Bearer ${token}` } }); } catch (err) { console.error(err); }
                    }
                    if (empresaNovaId) {
                        try { await axios.post(`/api/empresas/${empresaNovaId}/utilizadores`, { userId: selectedUserDetailsLocal._id }, { headers: { Authorization: `Bearer ${token}` } }); } catch (err) { console.error(err); }
                    }
                    window.dispatchEvent(new CustomEvent('associacaoEmpresaUtilizadorAlterada', {
                        detail: { empresaId: empresaAntigaId || empresaNovaId, userId: selectedUserDetailsLocal._id, action: empresaNovaId ? 'addUser' : 'removeUser', timestamp: Date.now() }
                    }));
                }

                if (editSelectedImageLocal) {
                    setIsUploadingImageLocal(true);
                    try { await uploadImage(selectedUserDetailsLocal._id, editSelectedImageLocal); showTempToast('Sucesso', 'Imagem atualizada!', 'success'); }
                    catch (err) { showTempToast('Aviso', 'Utilizador atualizado, mas falha ao enviar imagem', 'warning'); }
                    finally { setIsUploadingImageLocal(false); }
                }

                showTempToast('Sucesso', 'Utilizador atualizado com sucesso!', 'success');
                await fetchUserDetailsLocal(selectedUserDetailsLocal._id, selectedUserDetailsLocal.nome);
                setIsEditingUserLocal(false);
                setEditSelectedImageLocal(null);
                setEditImagePreviewLocal(null);
                setShowPasswordFieldLocal(false);
                setEditUserDataLocal(prev => ({ ...prev, senha: '' }));
                await fetchUsers();
                await atualizarLoggedUser();
                notificarAtualizacaoUtilizadores();
            } else {
                const errorMsg = response.data?.mensagem || response.data?.erro || 'Erro ao atualizar utilizador';
                showTempToast('Erro', errorMsg, 'danger');
            }
        } catch (err) {
            let errorMessage = 'Erro ao atualizar utilizador';
            if (err.response?.status === 403) errorMessage = err.response?.data?.erro || 'Não tem permissão para editar este utilizador';
            else if (err.response?.status === 404) errorMessage = 'Utilizador não encontrado';
            else if (err.response?.data?.erro) errorMessage = err.response.data.erro;
            else if (err.response?.data?.message) errorMessage = err.response.data.message;
            showTempToast('Erro', errorMessage, 'danger');
        } finally { setIsLoadingUserDetailsLocal(false); }
    };

    const openDeleteConfirmModal = () => { setUserToDelete(selectedUserDetailsLocal); setShowDeleteConfirmModal(true); };
    const closeDeleteConfirmModal = () => { setShowDeleteConfirmModal(false); setUserToDelete(null); };

    const confirmDeleteUser = async () => {
        if (!userToDelete) return;
        if (!podeApagarUtilizador()) { showTempToast('Erro', 'Não tem permissão para apagar este utilizador', 'danger'); return; }
        setIsLoadingUserDetailsLocal(true);
        setShowDeleteConfirmModal(false);
        try {
            const token = localStorage.getItem('token');
            if (!token) { showTempToast('Erro', 'Sessão expirada.', 'danger'); setIsLoadingUserDetailsLocal(false); return; }
            const empresaId = extrairEmpresaId(userToDelete.empresaId) || extrairEmpresaId(userToDelete.empresa) || null;
            if (empresaId) {
                try { await axios.delete(`/api/empresas/${empresaId}/utilizadores/${userToDelete._id}`, { headers: { Authorization: `Bearer ${token}` } }); } catch (err) { console.error(err); }
            }
            const response = await axios.delete(`/api/auth/users/${userToDelete._id}`, { headers: { Authorization: `Bearer ${token}` } });
            if (response.data) {
                showTempToast('Sucesso', `Utilizador ${userToDelete.nome} apagado com sucesso!`, 'success');
                setShowUserDetailsModalLocal(false);
                setIsEditingUserLocal(false);
                setSelectedUserDetailsLocal(null);
                setUserToDelete(null);
                await fetchUsers();
                await atualizarLoggedUser();
                notificarAtualizacaoUtilizadores();
            }
        } catch (err) {
            let errorMessage = 'Erro ao apagar utilizador';
            if (err.response?.status === 403) errorMessage = err.response?.data?.erro || 'Não tem permissão para apagar este utilizador';
            else if (err.response?.status === 404) errorMessage = 'Utilizador não encontrado';
            else if (err.response?.data?.erro) errorMessage = err.response.data.erro;
            else if (err.response?.data?.message) errorMessage = err.response.data.message;
            showTempToast('Erro', errorMessage, 'danger');
        } finally { setIsLoadingUserDetailsLocal(false); }
    };

    const addNewUser = async () => {
        if (!newUserLocal.nome.trim()) { setUserFormErrorLocal('Por favor, insira o nome do utilizador'); return; }
        if (newUserLocal.nome.length < 3) { setUserFormErrorLocal('O nome deve ter no mínimo 3 caracteres'); return; }
        if (!newUserLocal.email.trim()) { setUserFormErrorLocal('Por favor, insira o email do utilizador'); return; }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newUserLocal.email)) { setUserFormErrorLocal('Por favor, insira um email válido'); return; }
        if (!newUserLocal.senha.trim()) { setUserFormErrorLocal('Por favor, insira uma senha para o utilizador'); return; }
        if (newUserLocal.senha.length < 6) { setUserFormErrorLocal('A senha deve ter no mínimo 6 caracteres'); return; }

        if (!isSuperadmin) {
            if (limiteUtilizadores.limite === 0) { setUserFormErrorLocal('Esta empresa não tem uma licença ativa. Contacte o administrador.'); return; }
            if (limiteUtilizadores.disponivel <= 0) { setUserFormErrorLocal(`Limite de utilizadores atingido (${limiteUtilizadores.utilizadoresAtuais}/${limiteUtilizadores.limite}). Contacte o administrador.`); return; }
        }

        setIsAddingUserLocal(true);
        try {
            const token = localStorage.getItem('token');
            if (!token) { setUserFormErrorLocal('Sessão expirada. Faça login novamente.'); setIsAddingUserLocal(false); return; }
            const user = loggedUser || JSON.parse(localStorage.getItem('usuario') || '{}');
            let empresaIdParaSalvar = newUserLocal.empresaId;
            if (!isSuperadmin) {
                empresaIdParaSalvar = extrairEmpresaId(user.empresaId) || extrairEmpresaId(user.empresa) || null;
                if (!empresaIdParaSalvar) { setUserFormErrorLocal('Você não está associado a nenhuma empresa. Contacte o administrador.'); setIsAddingUserLocal(false); return; }
            }
            const selectedEmpresa = empresasList.find(e => e._id === empresaIdParaSalvar);
            const response = await axios.post('/api/auth/register', {
                nome: newUserLocal.nome,
                email: newUserLocal.email.toLowerCase(),
                senha: newUserLocal.senha,
                perfil: newUserLocal.perfil,
                empresaId: empresaIdParaSalvar || null,
                empresa: selectedEmpresa?.nome || newUserLocal.empresaNome
            }, { headers: { Authorization: `Bearer ${token}` } });

            if (response.data) {
                const newUserCreated = response.data.user;
                if (empresaIdParaSalvar) {
                    await gerenciarAssociacaoEmpresa(newUserCreated._id, empresaIdParaSalvar, selectedEmpresa?.nome);
                    if (!isSuperadmin) await carregarLimiteUtilizadores(empresaIdParaSalvar);
                }
                if (selectedImageLocal) {
                    setIsUploadingImageLocal(true);
                    try { await uploadImage(newUserCreated._id, selectedImageLocal); showTempToast('Sucesso', 'Imagem enviada!', 'success'); }
                    catch (err) { showTempToast('Aviso', 'Utilizador criado, mas falha ao enviar imagem', 'warning'); }
                    finally { setIsUploadingImageLocal(false); }
                }
                showTempToast('Sucesso', `Utilizador ${newUserLocal.nome} criado com sucesso!`, 'success');
                setShowUserModalLocal(false);
                setSelectedImageLocal(null);
                setImagePreviewLocal(null);
                setNewUserLocal({ nome: '', email: '', perfil: 'Cliente', empresaId: '', empresaNome: '', senha: '' });
                await fetchUsers();
                await atualizarLoggedUser();
                notificarAtualizacaoUtilizadores();
            }
        } catch (err) {
            if (err.response?.data?.erro) setUserFormErrorLocal(err.response.data.erro);
            else if (err.response?.data?.message) setUserFormErrorLocal(err.response.data.message);
            else setUserFormErrorLocal('Erro ao conectar ao servidor. Tente novamente.');
        } finally { setIsAddingUserLocal(false); }
    };

    const getEmpresasParaSelect = () => {
        if (isSuperadmin) return empresasList;
        const user = loggedUser || JSON.parse(localStorage.getItem('usuario') || '{}');
        const empresaId = extrairEmpresaId(user.empresaId) || extrairEmpresaId(user.empresa);
        if (!empresaId) return [];
        return empresasList.filter(e => e._id === empresaId);
    };

    const displayedUsers = showAllUsersLocal ? usersList : usersList.slice(0, 3);
    const hasMoreUsers = usersList.length > 3;
    const podeAdicionar = podeAdicionarUtilizador();
    const limiteMessage = getLimiteMessage();

    // ==============================================
    // RENDER MODAIS
    // ==============================================
    const renderDeleteConfirmModal = () => {
        if (!showDeleteConfirmModal) return null;
        return (
            <div className="delete-confirm-modal-overlay" onClick={closeDeleteConfirmModal}>
                <div className="delete-confirm-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="delete-confirm-modal-icon">
                        <Icon name="error" size={56} color="var(--color-danger)" />
                    </div>
                    <h3>Apagar Utilizador</h3>
                    <p>Tem certeza que deseja apagar o utilizador <strong>"{userToDelete?.nome}"</strong>?<br />Esta ação não pode ser desfeita.</p>
                    <div className="delete-confirm-buttons">
                        <button className="delete-confirm-cancel" onClick={closeDeleteConfirmModal}>
                            <Icon name="close" size={16} /> Cancelar
                        </button>
                        <button className="delete-confirm-confirm" onClick={confirmDeleteUser}>
                            <Icon name="delete" size={16} /> Apagar
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const renderUserDetailsModal = () => {
        if (!showUserDetailsModalLocal) return null;
        const podeEditar = podeEditarUtilizador();
        const podeApagar = podeApagarUtilizador();
        const selectHabilitado = isSuperadmin || loggedUser?.perfil === 'Admin';
        return (
            <div className="modal-overlay" onClick={() => { setShowUserDetailsModalLocal(false); setIsEditingUserLocal(false); setShowPasswordFieldLocal(false); }}>
                <div className="modal-content modal-details" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-header">
                        <h3>
                            <Icon name="person" size={20} />
                            {isEditingUserLocal ? 'Editar Utilizador' : 'Detalhes do Utilizador'}
                            <span className="modal-user-name">{selectedUserDetailsLocal?.nome}</span>
                        </h3>
                        <button className="modal-close" onClick={() => { setShowUserDetailsModalLocal(false); setIsEditingUserLocal(false); setShowPasswordFieldLocal(false); }}>
                            <Icon name="close" size={18} />
                        </button>
                    </div>
                    {isLoadingUserDetailsLocal ? (
                        <div className="modal-body loading"><p>A carregar...</p></div>
                    ) : (
                        <div className="modal-body">
                            <div className="user-details-section">
                                <h4 className="section-title">
                                    <span><Icon name="description" size={18} /> Informações Pessoais</span>
                                    {!isEditingUserLocal && (
                                        <button className={`edit-btn ${podeEditar ? 'enabled' : 'disabled'}`} onClick={() => podeEditar ? setIsEditingUserLocal(true) : null} disabled={!podeEditar}>
                                            <Icon name="edit" size={14} /> Editar
                                        </button>
                                    )}
                                </h4>
                                <div className="form-group">
                                    <label>Imagem de Perfil</label>
                                    <div className="profile-image-container">
                                        <div className="profile-image-wrapper">
                                            {isEditingUserLocal ? (
                                                editImagePreviewLocal ? <img src={editImagePreviewLocal} alt="Preview" className="profile-image-preview" /> :
                                                    selectedUserDetailsLocal?.imagemPerfil ? <img src={getImagemUrl(selectedUserDetailsLocal.imagemPerfil)} alt="Perfil" className="profile-image-preview" /> :
                                                        <Icon name="customers" size={40} color="var(--color-info-dark)" />
                                            ) : selectedUserDetailsLocal?.imagemPerfil ? <img src={getImagemUrl(selectedUserDetailsLocal.imagemPerfil)} alt="Perfil" className="profile-image-preview" /> : <Icon name="customers" size={40} color="var(--color-info-dark)" />}
                                        </div>
                                        {isEditingUserLocal && (
                                            <div className="profile-image-actions">
                                                <input type="file" id="editProfileImage" accept="image/*" style={{ display: 'none' }} onChange={(e) => handleImageChange(e, true)} />
                                                <button type="button" className="btn-select-image" onClick={() => document.getElementById('editProfileImage').click()}>
                                                    <Icon name="image" size={14} /> Selecionar Imagem
                                                </button>
                                                {editImagePreviewLocal && (
                                                    <button type="button" className="btn-remove-image" onClick={() => { setEditSelectedImageLocal(null); setEditImagePreviewLocal(null); }}>
                                                        <Icon name="delete" size={14} /> Remover
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    {isEditingUserLocal && <small className="form-help">Formatos aceitos: JPG, PNG, GIF. Máximo 5MB</small>}
                                </div>
                                <div className="detail-row">
                                    <span className="detail-label">Nome:</span>
                                    {isEditingUserLocal ? <input type="text" className="detail-input" value={editUserDataLocal.nome} onChange={(e) => setEditUserDataLocal({ ...editUserDataLocal, nome: e.target.value })} /> : <span className="detail-value">{selectedUserDetailsLocal?.nome}</span>}
                                </div>
                                <div className="detail-row">
                                    <span className="detail-label">Email:</span>
                                    {isEditingUserLocal ? <input type="email" className="detail-input" value={editUserDataLocal.email} onChange={(e) => setEditUserDataLocal({ ...editUserDataLocal, email: e.target.value })} /> : <span className="detail-value">{selectedUserDetailsLocal?.email}</span>}
                                </div>
                                <div className="detail-row empresa-row">
                                    <span className="detail-label">Empresa:</span>
                                    <div className="empresa-field-wrapper">
                                        {isEditingUserLocal ? (
                                            <div className="empresa-edit-container">
                                                <select className="empresa-select" value={editUserDataLocal.empresaId || ""} onChange={(e) => {
                                                    const empresaId = e.target.value;
                                                    const empresa = empresasList.find(emp => emp._id === empresaId);
                                                    setEditUserDataLocal({ ...editUserDataLocal, empresaId, empresaNome: empresa?.nome || '' });
                                                }} disabled={!selectHabilitado}>
                                                    <option value="">Sem empresa</option>
                                                    {isSuperadmin ? empresasList.map(emp => <option key={emp._id} value={emp._id}>{emp.nome}</option>) :
                                                        loggedUser?.perfil === 'Admin' ? (() => {
                                                            const empresaAdminId = extrairEmpresaId(loggedUser?.empresaId) || extrairEmpresaId(loggedUser?.empresa);
                                                            const empresa = empresasList.find(e => e._id === empresaAdminId);
                                                            return empresa ? <option key={empresa._id} value={empresa._id}>{empresa.nome}</option> : null;
                                                        })() :
                                                            editUserDataLocal.empresaId && <option value={editUserDataLocal.empresaId}>{editUserDataLocal.empresaNome || "Empresa atual"}</option>}
                                                </select>
                                            </div>
                                        ) : (
                                            <span className="detail-value">{selectedUserDetailsLocal?.empresaNome || (selectedUserDetailsLocal?.empresa && typeof selectedUserDetailsLocal.empresa === 'object' ? selectedUserDetailsLocal.empresa.nome : selectedUserDetailsLocal?.empresa) || '-'}</span>
                                        )}
                                    </div>
                                </div>
                                <div className="detail-row">
                                    <span className="detail-label">Data de Criação:</span>
                                    <span className="detail-value">{selectedUserDetailsLocal?.dataCriacao ? new Date(selectedUserDetailsLocal.dataCriacao).toLocaleDateString('pt-PT') : '-'}</span>
                                </div>
                                {isEditingUserLocal && (
                                    <div className="detail-row password-row">
                                        <span className="detail-label">Senha:</span>
                                        <div className="password-field-wrapper">
                                            {!showPasswordFieldLocal ? (
                                                <button type="button" className="btn-change-password" onClick={() => setShowPasswordFieldLocal(true)}>
                                                    <Icon name="lock" size={14} /> Alterar Senha
                                                </button>
                                            ) : (
                                                <div className="password-input-container">
                                                    <input type="password" className="password-input" placeholder="Nova senha (mínimo 6 caracteres)" value={editUserDataLocal.senha} onChange={(e) => setEditUserDataLocal({ ...editUserDataLocal, senha: e.target.value })} />
                                                    <button type="button" className="btn-cancel-password" onClick={() => { setShowPasswordFieldLocal(false); setEditUserDataLocal({ ...editUserDataLocal, senha: '' }); }}>
                                                        <Icon name="close" size={12} /> Cancelar
                                                    </button>
                                                </div>
                                            )}
                                            <small className="form-help">Deixe em branco para manter a senha atual</small>
                                        </div>
                                    </div>
                                )}
                                <div className="detail-row">
                                    <span className="detail-label">Perfil:</span>
                                    {isEditingUserLocal ? (
                                        <select className="detail-select" value={editUserDataLocal.perfil} onChange={(e) => setEditUserDataLocal({ ...editUserDataLocal, perfil: e.target.value })}>
                                            {isSuperadmin ? (<><option value="Cliente">Cliente</option><option value="Contabilista">Contabilista</option><option value="Comercial">Comercial</option><option value="Admin">Admin</option><option value="Superadmin">Superadmin</option></>) :
                                                loggedUser?.perfil === 'Admin' ? (<><option value="Cliente">Cliente</option><option value="Contabilista">Contabilista</option><option value="Comercial">Comercial</option></>) :
                                                    <option value={selectedUserDetailsLocal?.perfil || 'Cliente'}>{selectedUserDetailsLocal?.perfil || 'Cliente'}</option>}
                                        </select>
                                    ) : <span className="detail-value"><span className={`status-badge ${selectedUserDetailsLocal?.perfil === 'Superadmin' ? 'active' : ''}`}>{selectedUserDetailsLocal?.perfil}</span></span>}
                                </div>
                                <div className="detail-row">
                                    <span className="detail-label">ID:</span>
                                    <span className="detail-value user-id">{selectedUserDetailsLocal?._id}</span>
                                </div>
                            </div>
                        </div>
                    )}
                    <div className="modal-actions">
                        {isEditingUserLocal ? (
                            <>
                                <button className="modal-btn-secondary" onClick={() => { setIsEditingUserLocal(false); setShowPasswordFieldLocal(false); setEditUserDataLocal(prev => ({ ...prev, senha: '' })); }}>
                                    <Icon name="close" size={14} /> Cancelar
                                </button>
                                <button className="modal-btn-primary" onClick={updateUser} disabled={isLoadingUserDetailsLocal}>
                                    <Icon name="save" size={14} />
                                    {isLoadingUserDetailsLocal ? (isUploadingImageLocal ? 'A enviar imagem...' : 'A guardar...') : 'Guardar Utilizador'}
                                </button>
                            </>
                        ) : (
                            <>
                                <button className="modal-btn-secondary" onClick={() => { setShowUserDetailsModalLocal(false); setIsEditingUserLocal(false); setShowPasswordFieldLocal(false); }}>
                                    <Icon name="close" size={14} /> Fechar
                                </button>
                                {podeApagar && (
                                    <button className="modal-btn-danger" onClick={openDeleteConfirmModal}>
                                        <Icon name="delete" size={14} /> Apagar Utilizador
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const renderUserModal = () => {
        if (!showUserModalLocal) return null;
        if (!podeAdicionar && !isSuperadmin) { showTempToast('Limite Atingido', limiteMessage || 'Não é possível adicionar mais utilizadores.', 'warning'); setShowUserModalLocal(false); return null; }
        const selectHabilitado = isSuperadmin;
        return (
            <div className="modal-overlay" onClick={() => { setShowUserModalLocal(false); setSelectedImageLocal(null); setImagePreviewLocal(null); }}>
                <div className="modal-content modal-add-user" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-header">
                        <h3><Icon name="person_add" size={20} /> Adicionar Novo Utilizador</h3>
                        <button className="modal-close" onClick={() => { setShowUserModalLocal(false); setSelectedImageLocal(null); setImagePreviewLocal(null); }}>
                            <Icon name="close" size={18} />
                        </button>
                    </div>
                    <div className="modal-body">
                        {userFormErrorLocal && (
                            <div className="form-error">
                                <Icon name="warning" size={16} /> {userFormErrorLocal}
                            </div>
                        )}
                        {!isSuperadmin && limiteMessage && (
                            <div className={`limite-alert ${limiteUtilizadores.disponivel <= 0 ? 'limite-alert-danger' : 'limite-alert-success'}`}>
                                {limiteMessage}
                            </div>
                        )}
                        <div className="form-group">
                            <label>Imagem de Perfil</label>
                            <div className="profile-image-container">
                                <div className="profile-image-wrapper">
                                    {imagePreviewLocal ? <img src={imagePreviewLocal} alt="Preview" className="profile-image-preview" /> : <Icon name="customers" size={40} color="var(--color-info-dark)" />}
                                </div>
                                <div className="profile-image-actions">
                                    <input type="file" id="profileImage" accept="image/*" style={{ display: 'none' }} onChange={(e) => handleImageChange(e, false)} />
                                    <button type="button" className="btn-select-image" onClick={() => document.getElementById('profileImage').click()}>
                                        <Icon name="image" size={14} /> Selecionar Imagem
                                    </button>
                                    {imagePreviewLocal && (
                                        <button type="button" className="btn-remove-image" onClick={() => { setSelectedImageLocal(null); setImagePreviewLocal(null); }}>
                                            <Icon name="delete" size={14} /> Remover
                                        </button>
                                    )}
                                </div>
                            </div>
                            <small className="form-help">Formatos aceitos: JPG, PNG, GIF. Máximo 5MB</small>
                        </div>
                        <div className="form-group">
                            <label>Nome completo *</label>
                            <input type="text" className="form-input" placeholder="Ex: João Silva" value={newUserLocal.nome} onChange={(e) => { setNewUserLocal({ ...newUserLocal, nome: e.target.value }); setUserFormErrorLocal(''); }} />
                        </div>
                        <div className="form-group">
                            <label>Email *</label>
                            <input type="email" className="form-input" placeholder="exemplo@dominio.com" value={newUserLocal.email} onChange={(e) => { setNewUserLocal({ ...newUserLocal, email: e.target.value }); setUserFormErrorLocal(''); }} />
                        </div>
                        <div className="form-group">
                            <label>Empresa</label>
                            <select className="form-select" value={newUserLocal.empresaId || ""} onChange={(e) => {
                                const empresaId = e.target.value;
                                const empresa = empresasList.find(emp => emp._id === empresaId);
                                setNewUserLocal({ ...newUserLocal, empresaId, empresaNome: empresa?.nome || '' });
                                setUserFormErrorLocal('');
                            }} disabled={!selectHabilitado}>
                                <option value="">Sem empresa</option>
                                {isSuperadmin ? empresasList.map(emp => <option key={emp._id} value={emp._id}>{emp.nome}</option>) : newUserLocal.empresaId && <option value={newUserLocal.empresaId}>{newUserLocal.empresaNome || "Empresa atual"}</option>}
                            </select>
                            {!isSuperadmin && newUserLocal.empresaNome && (
                                <small className="empresa-info success">
                                    <Icon name="check_circle" size={12} /> Empresa: {newUserLocal.empresaNome}
                                </small>
                            )}
                            {!isSuperadmin && !newUserLocal.empresaNome && (
                                <small className="empresa-info info">
                                    <Icon name="refresh" size={12} /> A empresa será carregada automaticamente...
                                </small>
                            )}
                        </div>
                        <div className="form-group">
                            <label>Senha *</label>
                            <input type="password" className="form-input" placeholder="Mínimo 6 caracteres" value={newUserLocal.senha} onChange={(e) => { setNewUserLocal({ ...newUserLocal, senha: e.target.value }); setUserFormErrorLocal(''); }} />
                        </div>
                        <div className="form-group">
                            <label>Perfil</label>
                            <select className="form-select" value={newUserLocal.perfil} onChange={(e) => setNewUserLocal({ ...newUserLocal, perfil: e.target.value })} disabled={!isSuperadmin}>
                                <option value="Cliente">Cliente</option>
                                <option value="Contabilista">Contabilista</option>
                                <option value="Comercial">Comercial</option>
                                <option value="Admin">Admin</option>
                                <option value="Superadmin">Superadmin</option>
                            </select>
                            {!isSuperadmin && <small className="form-help">Apenas Superadmin pode atribuir perfis especiais</small>}
                        </div>
                    </div>
                    <div className="modal-actions">
                        <button className="modal-btn-secondary" onClick={() => { setShowUserModalLocal(false); setSelectedImageLocal(null); setImagePreviewLocal(null); }}>
                            <Icon name="close" size={14} /> Cancelar
                        </button>
                        <button className="modal-btn-primary" onClick={addNewUser} disabled={isAddingUserLocal}>
                            <Icon name="add" size={14} />
                            {isAddingUserLocal ? (isUploadingImageLocal ? 'A enviar imagem...' : 'A adicionar...') : 'Adicionar Utilizador'}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const renderTempToast = () => {
        if (!tempToastLocal) return null;
        return (
            <div className={`toast-notification ${tempToastLocal.type}`}>
                <div className={`toast-icon ${tempToastLocal.type}`}>
                    <Icon
                        name={
                            tempToastLocal.type === 'success' ? 'check_circle' :
                            tempToastLocal.type === 'warning' ? 'warning' :
                            tempToastLocal.type === 'danger' ? 'error' : 'info'
                        }
                        size={20}
                    />
                </div>
                <div className="toast-content">
                    <div className="toast-title">{tempToastLocal.title}</div>
                    <div className="toast-message">{tempToastLocal.message}</div>
                </div>
                <button className="toast-close" onClick={() => setTempToastLocal(null)}>
                    <Icon name="close" size={16} />
                </button>
            </div>
        );
    };

    // ==============================================
    // 🔥 RENDER — VISTA SUPERADMIN (VENDEDOR)
    // ==============================================
    const renderVistaSuperadmin = () => {
        const m = metricasSaas;
        const totalPlanos = Object.values(m.planos).reduce((a, b) => a + b, 0) || 1;

        return (
            <>
                {/* === KPIs SaaS === */}
                <div className="analyse">
                    <div className="card sales">
                        <div className="status">
                            <div className="info">
                                <h3><Icon name="apartment" size={18} /> Total de Empresas</h3>
                                <h1>{m.totalEmpresas}</h1>
                                <small className="kpi-sub">{m.empresasAtivas} ativas · {m.empresasArquivadas} arquivadas</small>
                            </div>
                            <ProgressCircle percent={m.totalEmpresas > 0 ? Math.round((m.empresasAtivas / m.totalEmpresas) * 100) : 0} color="var(--color-success)" />
                        </div>
                    </div>

                    <div className="card visits">
                        <div className="status">
                            <div className="info">
                                <h3><Icon name="verified" size={18} /> Licenças Ativas</h3>
                                <h1>{m.licencasAtivas}</h1>
                                <small className="kpi-sub">{m.totalLicencas} no total · {m.licencasAExpirar} a expirar</small>
                            </div>
                            <ProgressCircle percent={m.totalLicencas > 0 ? Math.round((m.licencasAtivas / m.totalLicencas) * 100) : 0} color="var(--color-primary)" />
                        </div>
                    </div>

                    <div className="card searches">
                        <div className="status">
                            <div className="info">
                                <h3><Icon name="group" size={18} /> Utilizadores do Sistema</h3>
                                <h1>{m.totalUtilizadores}</h1>
                                <small className="kpi-sub">{m.utilizadoresAtivos} ativos · +{m.novos30d} nos últimos 30d</small>
                            </div>
                            <ProgressCircle percent={m.totalUtilizadores > 0 ? Math.round((m.utilizadoresAtivos / m.totalUtilizadores) * 100) : 0} color="var(--color-warning)" />
                        </div>
                    </div>
                </div>

                {/* === Alertas de Licenças === */}
                <div className="new-users">
                    <h2><Icon name="schedule" size={22} /> Licenças a Expirar (30 dias)</h2>
                    <LicencasAExpirar licencas={licencasList} />
                </div>

                {/* === Distribuição por Plano === */}
                <div className="new-users">
                    <h2><Icon name="card_membership" size={22} /> Distribuição por Plano</h2>
                    <div className="planos-grid">
                        {Object.entries(m.planos).map(([plano, qtd]) => {
                            const percent = Math.round((qtd / totalPlanos) * 100);
                            const colors = {
                                Trial: 'var(--color-warning)',
                                Basico: 'var(--color-primary)',
                                Profissional: 'var(--color-success)',
                                Enterprise: 'var(--color-danger)',
                                Custom: 'var(--color-info-dark)'
                            };
                            return (
                                <div className="plano-card" key={plano}>
                                    <div className="plano-header">
                                        <span className="plano-nome"><Icon name="star" size={14} /> {plano}</span>
                                        <span className="plano-qtd">{qtd}</span>
                                    </div>
                                    <div className="plano-bar">
                                        <div className="plano-bar-fill" style={{ width: `${percent}%`, backgroundColor: colors[plano] || 'var(--color-primary)' }} />
                                    </div>
                                    <small className="plano-percent">{percent}% do total</small>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* === Top Empresas por Utilizadores === */}
                <div className="new-users">
                    <h2><Icon name="workspace_premium" size={22} /> Top Empresas por Utilizadores</h2>
                    <TopEmpresas empresas={empresasList} usersList={usersList} getImagemUrl={getImagemUrl} />
                </div>
            </>
        );
    };

    // ==============================================
    // 🔥 RENDER — VISTA ADMIN (Empresa)
    // ==============================================
    const renderVistaAdmin = () => {
        return (
            <>
                <div className="analyse">
                    <div className="card sales">
                        <div className="status">
                            <div className="info">
                                <h3><Icon name="event_available" size={18} /> Vagas Disponíveis</h3>
                                <h1>{limiteUtilizadores.disponivel || 0}</h1>
                                <small className="kpi-sub">de {limiteUtilizadores.limite || 0} disponíveis</small>
                            </div>
                            <ProgressCircle
                                percent={limiteUtilizadores.limite > 0 ? Math.round((limiteUtilizadores.disponivel / limiteUtilizadores.limite) * 100) : 0}
                                color={limiteUtilizadores.disponivel > 0 ? "var(--color-success)" : "var(--color-danger)"}
                            />
                        </div>
                    </div>

                    <div className="card visits">
                        <div className="status">
                            <div className="info">
                                <h3><Icon name="group" size={18} /> Utilizadores Atuais</h3>
                                <h1>{limiteUtilizadores.utilizadoresAtuais || 0}</h1>
                                <small className="kpi-sub">na sua empresa</small>
                            </div>
                            <ProgressCircle
                                percent={limiteUtilizadores.limite > 0 ? Math.round((limiteUtilizadores.utilizadoresAtuais / limiteUtilizadores.limite) * 100) : 0}
                                color="var(--color-primary)"
                            />
                        </div>
                    </div>

                    <div className="card searches">
                        <div className="status">
                            <div className="info">
                                <h3><Icon name="show_chart" size={18} /> Ocupação da Licença</h3>
                                <h1>{limiteUtilizadores.limite > 0 ? Math.round((limiteUtilizadores.utilizadoresAtuais / limiteUtilizadores.limite) * 100) : 0}%</h1>
                                <small className="kpi-sub">
                                    {limiteUtilizadores.limite > 0 && (limiteUtilizadores.utilizadoresAtuais / limiteUtilizadores.limite) < 0.8 ? 'Boa margem' : 'Atenção ao limite'}
                                </small>
                            </div>
                            <ProgressCircle
                                percent={limiteUtilizadores.limite > 0 ? Math.round((limiteUtilizadores.utilizadoresAtuais / limiteUtilizadores.limite) * 100) : 0}
                                color={limiteUtilizadores.limite > 0 && (limiteUtilizadores.utilizadoresAtuais / limiteUtilizadores.limite) < 0.8 ? "var(--color-success)" : "var(--color-warning)"}
                            />
                        </div>
                    </div>
                </div>
            </>
        );
    };

    // ==============================================
    // RENDER PRINCIPAL
    // ==============================================
    return (
        <>
            {isSuperadmin ? renderVistaSuperadmin() : renderVistaAdmin()}

            {/* === Novos Utilizadores (só para Admin) === */}
            {!isSuperadmin && (
                <div className="new-users">
                    <h2><Icon name="person_add" size={22} /> Novos Utilizadores</h2>

                    {limiteMessage && (
                        <div className={`limite-alerta ${limiteUtilizadores.disponivel <= 0 ? 'limite-alerta-danger' : 'limite-alerta-success'}`}>
                            <Icon name={limiteUtilizadores.disponivel <= 0 ? 'warning' : 'info'} size={18} />
                            <span className="limite-alerta-mensagem">{limiteMessage}</span>
                        </div>
                    )}

                    <div className="user-list">
                        <div className="user add-user-card" onClick={() => {
                            if (podeAdicionar || isSuperadmin) setShowUserModalLocal(true);
                            else showTempToast('Limite Atingido', limiteMessage || 'Não é possível adicionar mais utilizadores.', 'warning');
                        }}>
                            <div className="add-user">
                                <Icon name="add" size={32} color="var(--color-dark-variant)" />
                            </div>
                            <h3>Mais</h3>
                            <small>{podeAdicionar ? 'Novo Utilizador' : 'Limite Atingido'}</small>
                        </div>

                        {displayedUsers.length > 0 ? (
                            displayedUsers.map(u => {
                                const userId = u._id || u.id;
                                const nome = u.nome || u.name || 'Utilizador';
                                let empresaNome = 'Sem empresa';
                                if (u.empresaNome) empresaNome = u.empresaNome;
                                else if (u.empresa) {
                                    if (typeof u.empresa === 'object' && u.empresa.nome) empresaNome = u.empresa.nome;
                                    else if (typeof u.empresa === 'string') empresaNome = u.empresa;
                                }
                                const dataCriacao = u.dataCriacao || u.createdAt || u.created_at || null;
                                let imagemUrl = null;
                                if (u.imagemPerfil || u.profileImage || u.avatar) {
                                    const imgSrc = u.imagemPerfil || u.profileImage || u.avatar;
                                    if (imgSrc && imgSrc !== 'null' && imgSrc !== 'undefined' && imgSrc !== '') imagemUrl = getImagemUrl(imgSrc);
                                }
                                return (
                                    <div className="user" key={userId || Math.random()} onClick={() => userId ? fetchUserDetailsLocal(userId, nome) : null}>
                                        <Avatar name={nome} src={imagemUrl} size={70} />
                                        <h3>{nome}</h3>
                                        <span className="empresa-name">
                                            <Icon name="business" size={10} /> {empresaNome}
                                        </span>
                                        <span className="time-since">
                                            <Icon name="calendar_today" size={10} /> {getDaysSinceCreation(dataCriacao)}
                                        </span>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="user empty-user">
                                <div className="avatar-placeholder">
                                    <Icon name="customers" size={30} color="var(--color-info-dark)" />
                                </div>
                                <h3 style={{ color: 'var(--color-info-dark)' }}>Sem utilizadores</h3>
                                <small style={{ color: 'var(--color-info-dark)' }}>Adicione o primeiro</small>
                            </div>
                        )}
                    </div>
                    {hasMoreUsers && (
                        <button className="view-all-users" onClick={() => setShowAllUsersLocal(!showAllUsersLocal)}>
                            {showAllUsersLocal ? 'Mostrar menos' : `Mostrar todos (${usersList.length})`}
                            <Icon name={showAllUsersLocal ? 'chevron_left' : 'chevron_right'} size={16} />
                        </button>
                    )}
                </div>
            )}

            {renderUserModal()}
            {renderUserDetailsModal()}
            {renderDeleteConfirmModal()}
            {renderTempToast()}
        </>
    );
}

// ==============================================
// 🔥 SUBCOMPONENTE: Licenças a Expirar
// ==============================================
function LicencasAExpirar({ licencas }) {
    const agora = new Date();

    const lista = licencas
        .filter(l => l.ativa && l.dataFim)
        .map(l => {
            const dias = Math.ceil((new Date(l.dataFim) - agora) / (1000 * 60 * 60 * 24));

            let empresaNome = 'Empresa';
            if (typeof l.empresaId === 'object' && l.empresaId?.nome) {
                empresaNome = l.empresaId.nome;
            } else if (l.empresaNome) {
                empresaNome = l.empresaNome;
            } else if (typeof l.empresa === 'object' && l.empresa?.nome) {
                empresaNome = l.empresa.nome;
            } else if (typeof l.empresa === 'string') {
                empresaNome = l.empresa;
            }

            return { ...l, dias, empresaNome };
        })
        .filter(l => l.dias > 0 && l.dias <= 30)
        .sort((a, b) => a.dias - b.dias)
        .slice(0, 5);

    if (lista.length === 0) {
        return (
            <div className="licencas-empty">
                <Icon name="check_circle" size={40} color="var(--color-success)" />
                <p>Nenhuma licença a expirar nos próximos 30 dias</p>
            </div>
        );
    }

    return (
        <div className="licencas-alerta-lista">
            {lista.map((l, i) => {
                const urgente = l.dias <= 7;
                return (
                    <div className={`licenca-alerta-item ${urgente ? 'urgente' : ''}`} key={l._id || i}>
                        <div className="licenca-alerta-icon">
                            <Icon name={urgente ? 'error' : 'warning'} size={20} />
                        </div>
                        <div className="licenca-alerta-info">
                            <strong>{l.empresaNome}</strong>
                            <small>Plano: {l.plano} · Chave: {l.chave?.substring(0, 12)}…</small>
                        </div>
                        <div className="licenca-alerta-dias">
                            <strong>{l.dias}</strong>
                            <small>{l.dias === 1 ? 'dia' : 'dias'}</small>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

// ==============================================
// 🔥 SUBCOMPONENTE: Top Empresas por Utilizadores
// ==============================================
function TopEmpresas({ empresas, usersList, getImagemUrl }) {
    const ranking = empresas
        .map(e => {
            const qtd = usersList.filter(u => {
                const uEmp = u.empresaId?._id || u.empresaId || u.empresa?._id || u.empresa;
                return uEmp && uEmp.toString() === e._id.toString();
            }).length;
            return { ...e, qtd };
        })
        .sort((a, b) => b.qtd - a.qtd)
        .slice(0, 5);

    if (ranking.length === 0) {
        return (
            <div className="ranking-empty">
                <Icon name="apartment" size={40} color="var(--color-info-dark)" />
                <p>Nenhuma empresa registada.</p>
            </div>
        );
    }

    return (
        <div className="ranking-empresas">
            {ranking.map((e, i) => (
                <div className="ranking-item" key={e._id}>
                    <div className="ranking-pos">{i + 1}</div>
                    <div className="ranking-logo">
                        {e.imagemPerfil ? (
                            <img src={getImagemUrl(e.imagemPerfil)} alt={e.nome} />
                        ) : (
                            <Icon name="apartment" size={22} color="var(--color-primary)" />
                        )}
                    </div>
                    <div className="ranking-info">
                        <strong>{e.nome}</strong>
                        <small>{e.nomeCurto || e.slug || '—'}</small>
                    </div>
                    <div className="ranking-count">
                        <strong>{e.qtd}</strong>
                        <small>users</small>
                    </div>
                </div>
            ))}
        </div>
    );
}